// AuthContext.js
import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, runTransaction } from "firebase/firestore";
import { db } from "../utils/firebase";
import { setPersistence, browserLocalPersistence } from "firebase/auth";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  // —— 1) Track Firebase Auth user ——  
  const [currentUser, setCurrentUser] = useState(null);

  // —— 2) Track your Firestore username ——  
  const [username, setUsername] = useState("");
  const [loadingProfile, setLoadingProfile] = useState(true);

  // —— 3) Track your custom “role” if you still need it ——  
  const [userRole, setUserRole] = useState(() => {
    return sessionStorage.getItem("userRole") || null;
  });
  useEffect(() => {
    if (userRole) sessionStorage.setItem("userRole", userRole);
    else sessionStorage.removeItem("userRole");
  }, [userRole]);

  // —— 4) Wire up onAuthStateChanged, and fetch username from Firestore ——  
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (!user) {
        // logged out
        setUsername("");
        setLoadingProfile(false);
      } else {
        // logged in → fetch their profile document
        try {
          const userRef = doc(db, "users", user.uid);
          const snap = await getDoc(userRef);
          if (snap.exists() && snap.data().username) {
            setUsername(snap.data().username);
          } else {
            setUsername("");
          }
        } catch (err) {
          console.error("Failed to load username:", err);
          setUsername("");
        } finally {
          setLoadingProfile(false);
        }
      }
    });
    return unsubscribe;
  }, []);
  useEffect(() => {
    const auth = getAuth();
    setPersistence(auth, browserLocalPersistence).catch(console.error);
  }, []);
  // —— 5) Expose a helper to change username (with uniqueness check) ——  
  const updateUsername = useCallback(async (newName) => {
    if (!currentUser) throw new Error("Not authenticated");
    const uid = currentUser.uid;
    newName = newName.trim();
    if (!newName) throw new Error("Empty");

    await runTransaction(db, async (tx) => {
      const nameRef = doc(db, "usernames", newName);
      const nameSnap = await tx.get(nameRef);

      if (nameSnap.exists() && nameSnap.data().uid !== uid) {
        throw new Error("TAKEN");
      }

      const userRef = doc(db, "users", uid);
      const userSnap = await tx.get(userRef);
      const oldName = userSnap.exists() ? userSnap.data().username : null;

      if (oldName && oldName !== newName) {
        tx.delete(doc(db, "usernames", oldName));
      }

      tx.set(nameRef, { uid });
      tx.set(userRef, { username: newName }, { merge: true });
    });

    // locally reflect change
    setUsername(newName);
  }, [currentUser]);

  const value = useMemo(() => ({
    currentUser,
    username,
    setUsername,
    loadingProfile,
    userRole,
    setUserRole,
    updateUsername,
  }), [currentUser, username, loadingProfile, userRole, updateUsername]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
